import React, { useMemo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import TreemapModule from 'highcharts/modules/treemap';
import Icon, { IconSizes } from '../../../components/foundations/Icon';
import css from './WidgetArtifact.css';
import { deepMerge } from '../../../utils/object';
import UnsupportedArtifact from './UnsupportedArtifact';

TreemapModule(Highcharts);

const CHART_COLORS = ['#3A92E5', '#C496ED', '#5CB85C', '#FFAA20', '#E64D5F', '#00D0B2', '#EB3D89', '#FFD797'];

const STYLE_COLORS = {
    text: '#334155',
    textLight: '#64748B',
    gridLine: '#E2E8F0',
    border: '#CBD5E1',
    background: '#FFFFFF',
};

const DEFAULT_HIGHCHARTS_CONFIG = {
    colors: CHART_COLORS,
    chart: {
        backgroundColor: 'transparent',
        style: { fontFamily: 'inherit' },
        reflow: true,
    },
    credits: { enabled: false },
    title: {
        style: {
            color: STYLE_COLORS.text,
            fontWeight: '500',
        },
    },
    subtitle: { style: { color: STYLE_COLORS.textLight } },
    legend: {
        itemStyle: { color: STYLE_COLORS.text },
        itemHoverStyle: { color: '#3A92E5' },
    },
    tooltip: {
        backgroundColor: STYLE_COLORS.background,
        borderColor: STYLE_COLORS.border,
        style: { color: STYLE_COLORS.text },
    },
    plotOptions: {
        series: { animation: { duration: 1000 } },
        pie: {
            allowPointSelect: true,
            cursor: 'pointer',
            dataLabels: {
                enabled: true,
                format: '<b>{point.name}</b>: {point.percentage:.1f}%',
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
            showInLegend: true,
        },
        scatter: {
            tooltip: { pointFormat: '<b>{point.name}</b><br/>X: {point.x}<br/>Y: {point.y}' },
        },
        bubble: {
            dataLabels: {
                enabled: true,
                format: '{point.name}',
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
        },
        treemap: {
            dataLabels: {
                enabled: true,
                style: { color: STYLE_COLORS.text, textOutline: 'none' },
            },
        },
    },
    responsive: {
        rules: [
            {
                condition: {
                    maxWidth: 500,
                },
                chartOptions: {
                    legend: {
                        enabled: false,
                    },
                },
            },
        ],
    },
};

const WidgetArtifact = ({ artifact }) => {
    const chartRef = useRef(null);
    const containerRef = useRef(null);

    const options = useMemo(() => {
        return deepMerge(DEFAULT_HIGHCHARTS_CONFIG, artifact.data || {});
    }, [artifact.data]);

    useEffect(() => {
        if (!containerRef.current) return;

        const resizeObserver = new ResizeObserver(() => {
            if (chartRef.current && chartRef.current.chart) {
                chartRef.current.chart.reflow();
            }
        });

        resizeObserver.observe(containerRef.current);

        return () => {
            resizeObserver.disconnect();
        };
    }, []);

    if (!artifact.data) {
        return (
            <div className={css.placeholder}>
                <div className={css.placeholderHeader}>
                    <Icon name="chart-line" size={IconSizes.LARGE} />
                    <div className={css.placeholderTitle}>Widget Visualization</div>
                    <div className={css.placeholderText}>Widget configuration not available</div>
                </div>
            </div>
        );
    }

    return (
        <div ref={containerRef} className={css.chartContainer}>
            <HighchartsReact
                ref={chartRef}
                highcharts={Highcharts}
                options={options}
                containerProps={{ style: { width: '100%' } }}
            />
        </div>
    );
};

WidgetArtifact.propTypes = {
    artifact: PropTypes.shape({
        data: PropTypes.object,
    }).isRequired,
};

export default WidgetArtifact;
